import { fileUpdated } from '../../utils/activityStreams';

const {
  ModalTest: {
    default: Modal,
    actions: { toggleModal },
  },
} = window.Edocat.Components;
const { Publish, Backup, Check } = window.MaterialIcons;
const {
  Button,
  withStyles,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  TextField,
  Typography,
  CircularProgress,
} = window.Material;
const { Alert } = window.labMaterial;
import { Dispatch } from 'redux';
import { ReduxComponentInterface } from 'edocatbase/Global';
import { updateActivity, revertVersion, uploadNewVersion } from '../../actions';
import { last } from 'ramda';
import { getFile } from '../../utils';

interface NewVersionModalProps extends ReduxComponentInterface {
  items: any;
  classes: any;
  site: string;
  callback: Function;
  disallowedExtensions: string[];
  isOpen: boolean;
}

interface NewVersionModalState {
  majorVersion: boolean;
  description: string;
  file: any;
  status: 'initial' | 'done' | 'error';
  message: string;
  loading: boolean;
}

const styles = theme => ({
  modal: {
    width: '560px',
    textAlign: 'center',
  },
  disallowedTitle: {
    marginBottom: '20px',
  },
  input: {
    display: 'none',
  },
  radioGroup: {
    margin: theme.spacing(2, 0),
  },
  textField: {
    margin: theme.spacing(3, 0, 0),
  },
  alert: {
    justifyContent: 'center',
    margin: '1rem 0',
  },
});

class NewVersionModal extends window.React.PureComponent<NewVersionModalProps, NewVersionModalState> {
  constructor(props: NewVersionModalProps) {
    super(props);
    this.state = {
      majorVersion: false,
      description: '',
      file: props.items?.newFile || null,
      status: 'initial',
      message: '',
      loading: false,
    };
  }

  onModalClose = () => {
    const { actions, isOpen } = this.props;

    if (isOpen) {
      actions.toggleModal(false, 'newVersionModal');
    }
  };

  onUploadClick = (event: React.MouseEvent) => {
    event.preventDefault();

    this.setState(
      () => ({
        loading: true,
      }),
      async () => {
        const {
          items: { onUpload, index, originalFile, newFile, revertVersionData },
          site,
          callback,
        } = this.props;
        const { majorVersion, description, file } = this.state;

        const resolvedFile = file?.file ? await getFile(file) : file;

        const newVersionData = revertVersionData
          ? { majorVersion: majorVersion ? 'true' : 'false', comment: description }
          : { nodeRef: originalFile.nodeRef, fileName: originalFile.name, majorVersion, desc: description };

        if (revertVersionData) {
          try {
            await revertVersion(revertVersionData.nodeRef, revertVersionData.version, newVersionData);
          } catch (err:unknown) {
            if(err instanceof Error){
              if(Number(err.message) === 409 || 'Conflict'){
                this.setState({
                  status: 'error',
                  message: window.Alfresco.messages.global['edocat.revert-version.error'],
                  loading: false,
                });
              } else {
                this.setState({
                  status: 'error',
                  message: window.Alfresco.messages.global['edocat.new-version-upload.error'],
                  loading: false,
                });
              }
            } else {
              this.setState({
                status: 'error',
                message: window.Alfresco.messages.global['edocat.new-version-upload.error'],
                loading: false,
              });
            }
            return;
          }
        } else {
          if (newFile && onUpload) {
            //If the modal is called from UploadModal (new version upload after upload error).
            onUpload(null, [index], newVersionData);
          } else {
            //If the modal is called from outside (Upload component usage as a NewVersionModal itself).
            try {
              await uploadNewVersion(resolvedFile, originalFile.nodeRef, majorVersion, description, () => {});
              updateActivity(fileUpdated(originalFile.name, originalFile.nodeRef, site));
            } catch (e) {
              this.setState({
                status: 'error',
                message: e || window.Alfresco.messages.global['edocat.new-version-upload.error'],
                loading: false,
              });

              return;
            }
          }
        }

        const successMessage =
          window.Alfresco.messages.global[revertVersionData ? 'edocat.revert.success' : 'edocat.upload.success'];

        this.setState({
          status: 'done',
          message: successMessage,
          loading: false,
        });

        setTimeout(() => {
          this.onModalClose();
        }, 1000);

        callback && callback();
      }
    );
  };

  onDescriptionChange = (event: React.ChangeEvent) => {
    this.setState({
      description: event.target.value,
    });
  };

  onVersionTypeChange = (event: React.ChangeEvent) => {
    this.setState({
      majorVersion: event.target.value === 'true',
    });
  };

  onFilesAdded = (event: React.ChangeEvent) => {
    //This condition prevents IE from considering input value cleaning as no files selection, which breaks UploadModal.
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.setState({
        file,
        message: '',
      });
      //If we wouldn't do it, the input could work unpredictably, for example, not reopening file selection window.
      document.getElementById('uploadNewVersionInput').value = null;
    }
  };

  render() {
    const {
      items: {
        originalFile: { version },
        hideInput,
        revertVersionData,
      },
      disallowedExtensions,
      classes,
    } = this.props;
    const { file, majorVersion, description, status, message, loading } = this.state;

    const VERSION = version.split('.');

    const fileDisallowed = !file ? false : disallowedExtensions.includes(last(file.name.split('.')));

    const title = revertVersionData
      ? window.Alfresco.messages.global['button.undo']
      : window.Alfresco.messages.global['edocat.document-version.update'];

    const buttonLabel = revertVersionData
      ? window.Alfresco.messages.global['label.undo']
      : window.Alfresco.messages.global['actions.document.upload-new-version'];

    return (
      <Modal name="newVersionModal" title={title}>
        <div className={`modal-body ${classes.modal}`}>
          {fileDisallowed && (
            <Typography variant="h6" classes={{ root: classes.disallowedTitle }}>
              {window.Alfresco.messages.global['edocat.docDetail.disallowed-extensions.text']}
            </Typography>
          )}
          {!hideInput && !revertVersionData && (
            <React.Fragment>
              <input
                className={classes.input}
                onChange={this.onFilesAdded}
                id="uploadNewVersionInput"
                multiple={false}
                type="file"
              />
              <label htmlFor="uploadNewVersionInput">
                <Button startIcon={<Backup color={'primary'} />} component="span">
                  {file ? file.name : window.Alfresco.messages.global['label.upload-file']}
                </Button>
              </label>
            </React.Fragment>
          )}
          {status === 'error' && (
            <Alert classes={{ root: classes.alert }} severity="error">
              {message}
            </Alert>
          )}
          {status === 'done' && (
            <Alert classes={{ root: classes.alert }} severity="success">
              {message}
            </Alert>
          )}
          {!fileDisallowed && (
            <FormControl>
              <FormLabel align={'left'}>{window.Alfresco.messages.global['label.version']}</FormLabel>
              <RadioGroup className={classes.radioGroup}>
                <FormControlLabel
                  label={`${window.Alfresco.messages.global['edocat.document-version.smallUpdate']} (${VERSION[0]}.${
                    Number(VERSION[1]) + 1
                  })`}
                  value={false}
                  control={
                    <Radio
                      color={'primary'}
                      onChange={this.onVersionTypeChange}
                      name="majorVersion"
                      checked={!majorVersion}
                    />
                  }
                />
                <FormControlLabel
                  label={`${window.Alfresco.messages.global['edocat.document-version.bigUpdate']} (${
                    Number(VERSION[0]) + 1
                  }.0)`}
                  value={true}
                  control={
                    <Radio
                      color={'primary'}
                      name="majorVersion"
                      checked={majorVersion}
                      onChange={this.onVersionTypeChange}
                    />
                  }
                />
              </RadioGroup>
              <TextField
                className={classes.textField}
                label={window.Alfresco.messages.global['label.comments']}
                name="description"
                onChange={this.onDescriptionChange}
                value={description}
              />
            </FormControl>
          )}
        </div>
        <div className="modal-actions">
          <Button
            startIcon={loading ? <CircularProgress size="1rem" /> : status === 'done' ? <Check /> : <Publish />}
            color={'primary'}
            disabled={(!Boolean(file) && !Boolean(revertVersionData)) || fileDisallowed || loading}
            onClick={this.onUploadClick}
            id="uploadNewVersionBtn"
          >
            {buttonLabel}
          </Button>
          <Button onClick={this.onModalClose} disabled={loading} id="cancelUploadNewVersionBtn">
            {window.Alfresco.messages.global['button.close']}
          </Button>
        </div>
      </Modal>
    );
  }
}

const mapStateToProps = (state: any) => ({
  items: state.modal.newVersionModal?.items,
  isOpen: state.modal.newVersionModal?.isOpen,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: window.redux.bindActionCreators({ toggleModal }, dispatch),
});

export default window.reactRedux.connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(NewVersionModal));
